import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customInstance, useGetProjects } from '@edison/api-client-react';
import { AlertCircle, TriangleAlert, RotateCcw, CheckCircle2, X } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { SlidePanel } from '@/components/ui/slide-panel.js';
import { type EligibilityStatus } from './eligibility-chip.js';

// The 11 cost categories (lib/db/src/schema/cost-allocations.ts COST_CATEGORIES) with display
// labels. Exported so the manual-add panel in index.tsx renders the same set.
export const COST_CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'cloud', label: 'Cloud Hosting' },
  { value: 'llm_tokens', label: 'AI / LLM Services' },
  { value: 'software_license', label: 'Software Licences' },
  { value: 'hardware', label: 'Hardware & Equipment' },
  { value: 'other', label: 'Other' },
  { value: 'admin_overhead', label: 'Admin Overhead' },
  { value: 'software_subscription', label: 'Software Subscription' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'consultant_fees', label: 'Consultant Fees' },
  { value: 'company_setup', label: 'Company Setup' },
  { value: 'rdti_claim_prep', label: 'R&DTI Claim Prep' },
];

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  COST_CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
);

export interface CostCandidate {
  id: string;
  accountName: string;
  period: string; // YYYY-MM
  amount: string;
  suggestedCategory: string;
  status: 'pending' | 'approved' | 'dismissed';
  duplicateVendor: string | null;
}

function formatAUD(amount: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 }).format(amount);
}

function formatMonth(period: string): string {
  const [y, m] = period.split('-');
  if (!y || !m) return period;
  return new Date(`${y}-${m}-01T00:00:00Z`).toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Xero GL cost-candidate review queue. Pending candidates for the FY, with inline approve
 * (category + apportioned percent + eligibility + reason) and dismiss (reason required). In sim
 * mode the queue is always empty (no Xero snapshots) — the empty state is a first-class render.
 */
export function CandidateQueue({
  incomeYear,
  effectiveDefaultPercent,
  onChanged,
}: {
  incomeYear: string;
  effectiveDefaultPercent: number | null;
  onChanged: () => void;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [approveTarget, setApproveTarget] = useState<CostCandidate | null>(null);
  const [dismissTarget, setDismissTarget] = useState<CostCandidate | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const query = useQuery({
    queryKey: ['expenditure-candidates', incomeYear],
    queryFn: () =>
      customInstance<{ data: { items: CostCandidate[] } }>({
        url: '/api/expenditure/candidates',
        method: 'GET',
        params: { incomeYear, status: 'pending' },
      }).then((r) => r.data),
  });

  async function refresh() {
    setRefreshing(true);
    try {
      await customInstance({ url: '/api/expenditure/candidates/refresh', method: 'POST', data: { incomeYear } });
      await query.refetch();
    } finally {
      setRefreshing(false);
    }
  }

  const items = query.data?.items ?? [];

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="p-4 border-b bg-muted/30 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-medium text-sm">Xero cost review queue</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Ledger costs from Xero awaiting review before they enter the claim.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void refresh()} disabled={refreshing || query.isLoading}>
          <RotateCcw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
          {refreshing ? 'Refreshing…' : 'Refresh from Xero'}
        </Button>
      </div>

      {/* Post-approve warnings (e.g. possible double-count) — surfaced from the approve response. */}
      {warnings.length > 0 && (
        <div className="border-b border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 px-4 py-2.5">
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-1.5">
              <TriangleAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              {w}
            </p>
          ))}
        </div>
      )}

      {query.isLoading ? (
        <div className="p-4 space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="h-9 rounded skeleton" />)}
        </div>
      ) : query.error ? (
        <div className="p-4 text-sm text-destructive flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load the review queue.</span>
          <button onClick={() => void query.refetch()} className="underline text-xs">Retry</button>
        </div>
      ) : items.length === 0 ? (
        <div className="px-4 py-10 text-center">
          <CheckCircle2 className="h-6 w-6 text-muted-foreground/50 mx-auto" aria-hidden="true" />
          <p className="mt-3 text-sm text-muted-foreground">No unreviewed Xero costs for {incomeYear}.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Sync your Xero ledger, then refresh to surface overhead costs for review.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="text-left font-medium px-4 py-2">Account</th>
                <th className="text-left font-medium px-3 py-2">Period</th>
                <th className="text-right font-medium px-3 py-2">Amount</th>
                <th className="text-left font-medium px-3 py-2">Suggested category</th>
                <th className="text-right font-medium px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-xs">{c.accountName}</div>
                    {c.duplicateVendor && (
                      <span className="mt-1 inline-flex items-start gap-1 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900/50 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:text-amber-300">
                        <TriangleAlert className="h-3 w-3 shrink-0 mt-0.5" aria-hidden="true" />
                        Possible duplicate of {c.duplicateVendor} (API sync)
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{formatMonth(c.period)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs whitespace-nowrap">{formatAUD(parseFloat(c.amount))}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {CATEGORY_LABEL[c.suggestedCategory] ?? c.suggestedCategory}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <div className="inline-flex gap-2">
                      <Button variant="primary" size="sm" onClick={() => { setWarnings([]); setApproveTarget(c); }}>
                        Approve
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setDismissTarget(c)}>
                        Dismiss
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ApprovePanel
        candidate={approveTarget}
        effectiveDefaultPercent={effectiveDefaultPercent}
        onClose={() => setApproveTarget(null)}
        onApproved={(w) => {
          setApproveTarget(null);
          setWarnings(w);
          void query.refetch();
          onChanged();
        }}
      />

      <DismissPanel
        candidate={dismissTarget}
        onClose={() => setDismissTarget(null)}
        onDismissed={() => {
          setDismissTarget(null);
          void query.refetch();
          onChanged();
        }}
      />
    </div>
  );
}

// ─── Approve slide panel ─────────────────────────────────────────────────────

function ApprovePanel({
  candidate,
  effectiveDefaultPercent,
  onClose,
  onApproved,
}: {
  candidate: CostCandidate | null;
  effectiveDefaultPercent: number | null;
  onClose: () => void;
  onApproved: (warnings: string[]) => void;
}) {
  const [category, setCategory] = useState('');
  const [percent, setPercent] = useState('');
  const [eligibility, setEligibility] = useState<EligibilityStatus>('claimable');
  const [reason, setReason] = useState('');
  const [projectId, setProjectId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Re-seed the form whenever a new candidate is opened.
  const [seededFor, setSeededFor] = useState<string | null>(null);

  // Optional finer attribution — a specific project. Left blank means the cost rolls up to R&D
  // generally (eligibilityStatus is THE R&D determination; projectId is optional).
  const { data: projectsData } = useGetProjects();
  const projects = (projectsData?.data?.items ?? []) as Array<{ id: string; name: string; status?: string }>;
  const activeProjects = projects.filter((p) => p.status !== 'archived');

  if (candidate && seededFor !== candidate.id) {
    setSeededFor(candidate.id);
    setCategory(candidate.suggestedCategory);
    setPercent(effectiveDefaultPercent != null ? String(effectiveDefaultPercent) : '');
    setEligibility('claimable');
    setReason('');
    setProjectId('');
    setError(null);
  }

  // The value the percent field was seeded with from the apportionment default. If the reviewer
  // leaves it exactly as pre-filled, we OMIT claimablePercent from the request so the server stamps
  // the apportionment provenance (labour_pct / custom) rather than 'manual'. Only a real edit sends
  // an explicit percent.
  const prefillPercent = effectiveDefaultPercent != null ? String(effectiveDefaultPercent) : '';

  async function submit() {
    if (!candidate) return;
    // Reason is required when excluding — matches the server refine + the * on the field label.
    if (eligibility === 'excluded' && reason.trim() === '') {
      setError('A reason is required when excluding a cost.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { category, eligibilityStatus: eligibility };
      if (percent.trim() !== '' && percent !== prefillPercent) body['claimablePercent'] = parseFloat(percent);
      if (reason.trim() !== '') body['eligibilityReason'] = reason.trim();
      if (projectId !== '') body['projectId'] = projectId;

      const res = await customInstance<{ data: unknown; warnings?: string[] }>({
        url: `/api/expenditure/candidates/${candidate.id}/approve`,
        method: 'POST',
        data: body,
      });
      onApproved(res.warnings ?? []);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg ?? 'Failed to approve this cost. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const fieldCls = 'w-full rounded border bg-background px-3 py-2 text-sm';
  const labelCls = 'text-xs font-medium block mb-1';

  return (
    <SlidePanel open={candidate !== null} onClose={onClose} title="Approve Xero cost" width="w-[480px]">
      {candidate && (
        <div className="p-5 space-y-4">
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
            <div className="font-medium">{candidate.accountName}</div>
            <div className="text-muted-foreground mt-0.5">
              {formatMonth(candidate.period)} · {formatAUD(parseFloat(candidate.amount))}
            </div>
          </div>

          <div>
            <label className={labelCls} htmlFor="cq-category">Category</label>
            <select id="cq-category" className={fieldCls} value={category} onChange={(e) => setCategory(e.target.value)}>
              {COST_CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls} htmlFor="cq-project">
              Project <span className="text-muted-foreground font-normal">(optional — leave blank for R&amp;D general)</span>
            </label>
            <select id="cq-project" className={fieldCls} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">R&amp;D general (no project)</option>
              {activeProjects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls} htmlFor="cq-eligibility">Eligibility</label>
            <select
              id="cq-eligibility"
              className={fieldCls}
              value={eligibility}
              onChange={(e) => setEligibility(e.target.value as EligibilityStatus)}
            >
              <option value="claimable">Claimable</option>
              <option value="needs_review">Needs review</option>
              <option value="excluded">Excluded</option>
            </select>
          </div>

          <div>
            <label className={labelCls} htmlFor="cq-percent">
              Claimable percent {effectiveDefaultPercent != null && <span className="text-muted-foreground font-normal">(pre-filled from apportionment)</span>}
            </label>
            <input
              id="cq-percent"
              type="number"
              min="0"
              max="100"
              step="0.1"
              inputMode="decimal"
              className={fieldCls}
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              placeholder="e.g. 100"
            />
          </div>

          <div>
            <label className={labelCls} htmlFor="cq-reason">
              Reason / basis {eligibility === 'excluded' && <span className="text-destructive">*</span>}
            </label>
            <textarea
              id="cq-reason"
              className={`${fieldCls} resize-none`}
              rows={3}
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this eligibility / percent applies — recorded for audit."
            />
          </div>

          {error && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" aria-hidden="true" /> {error}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <Button variant="primary" className="flex-1" onClick={() => void submit()} disabled={saving}>
              {saving ? 'Approving…' : 'Approve cost'}
            </Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      )}
    </SlidePanel>
  );
}

// ─── Dismiss slide panel ─────────────────────────────────────────────────────

function DismissPanel({
  candidate,
  onClose,
  onDismissed,
}: {
  candidate: CostCandidate | null;
  onClose: () => void;
  onDismissed: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seededFor, setSeededFor] = useState<string | null>(null);

  if (candidate && seededFor !== candidate.id) {
    setSeededFor(candidate.id);
    setReason('');
    setError(null);
  }

  async function submit() {
    if (!candidate) return;
    if (reason.trim() === '') { setError('A dismiss reason is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await customInstance({
        url: `/api/expenditure/candidates/${candidate.id}/dismiss`,
        method: 'POST',
        data: { reason: reason.trim() },
      });
      onDismissed();
    } catch {
      setError('Failed to dismiss this cost. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SlidePanel open={candidate !== null} onClose={onClose} title="Dismiss Xero cost" width="w-[420px]">
      {candidate && (
        <div className="p-5 space-y-4">
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
            <div className="font-medium">{candidate.accountName}</div>
            <div className="text-muted-foreground mt-0.5">
              {formatMonth(candidate.period)} · {formatAUD(parseFloat(candidate.amount))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground flex items-start gap-1.5">
            <X className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
            Dismissed costs stay out of the queue across refreshes. The reason is recorded for audit.
          </p>
          <div>
            <label className="text-xs font-medium block mb-1" htmlFor="cq-dismiss-reason">
              Reason <span className="text-destructive">*</span>
            </label>
            <textarea
              id="cq-dismiss-reason"
              className="w-full rounded border bg-background px-3 py-2 text-sm resize-none"
              rows={3}
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Payroll clearing account — claimed via labour, not overhead."
            />
          </div>
          {error && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" aria-hidden="true" /> {error}
            </p>
          )}
          <div className="flex gap-2 pt-1">
            <Button variant="primary" className="flex-1" onClick={() => void submit()} disabled={saving}>
              {saving ? 'Dismissing…' : 'Dismiss cost'}
            </Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      )}
    </SlidePanel>
  );
}
